import React, { useEffect, useState } from "react";
import { Grid, Typography, Paper, Box, Stack, Chip } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CommonButton from "../../Components/InputComponents/CommonButton";
import { decryptId } from "../../Components/CryptoComponent";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";

const ClientJobDetails = () => {
  const { id } = useParams();
  const decryptedId = decryptId(decodeURIComponent(id));

  const location = useLocation();
  const navigate = useNavigate();

  const fromRoute = location.state?.fromRoute;


  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    title: "",
    country: "",
    qualification: "",
    profession: "",
    jobID: "",
  };

  const [jobdata, setJobData] = useState({});

  const getData = () => {
    axiosAPI
      ?.get(`/Job/listbyIdForView/${decryptedId}`)
      .then((res) => {
        setJobData(res?.data?.data);
      })
      .catch((err) => {});
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  return (
    <Paper
      sx={{ p: { xs: 4, sm: 3, md: 5 }, minHeight: "100%", maxWidth: "100%" }}
    >
      {/* Actions */}
      <Grid container justifyContent="flex-start" sx={{ mb: 3 }}>
        <Grid item>
          {fromRoute != "/Candidates/View" && (
            <CommonButton
              variant="contained"
              customColor="#B0BEC5"
              onClick={() => {
                navigate(`/Jobs`, {
                  state: {
                    pagination: paginationState,
                    filterData: filterData,
                  },
                });
              }}
            >
              Back
            </CommonButton>
          )}
        </Grid>
      </Grid>
      <Typography
        variant="h5"
        mb={1}
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Job Overview ({jobdata?.JD_JobId} )
      </Typography>

      {/* Job Information Section */}
      <Box
        sx={{
          mb: 3,
          border: "1px solid lightgrey",
          p: 3,
          borderRadius: "20px",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            px: 2,
            py: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          >
            Job Details
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {[
            {
              label: "Job Title",
              value: jobdata?.JD_Title || "-",
              gridSize: "small",
            },
            {
              label: "Department",
              value: jobdata?.JD_Department || "-",
              gridSize: "small",
            },
            {
              label: "Profession",
              value: (() => {
                try {
                  const profession = jobdata?.JD_Profession;
                  const parsed =
                    typeof profession == "string"
                      ? JSON.parse(profession)
                      : profession;
                  return parsed?.Name || "-";
                } catch {
                  return "-";
                }
              })(),
              gridSize: "small",
            },
            {
              label: "Country",
              value: (() => {
                try {
                  const country = jobdata?.JD_Country;
                  const parsed =
                    typeof country == "string" ? JSON.parse(country) : country;
                  return parsed?.Name || "-";
                } catch {
                  return "-";
                }
              })(),
              gridSize: "small",
            },

            {
              label: "Location (Branch)",
              value: (() => {
                try {
                  return jobdata?.JD_Locations || jobdata?.JD_Branch || "-";
                } catch {
                  return "-";
                }
              })(),
              gridSize: "small",
            },
            {
              label: "Salary Range",
              value: (() => {
                const currency = jobdata?.JD_Currency
                  ? jobdata?.JD_Currency
                  : `-`;
                const code = currency?.Code || "";
                const SalaryRange = jobdata?.JD_SalaryRange;
                const SalaryPay = jobdata?.JD_SalaryPay;

                if (SalaryRange)
                  return `${SalaryRange} ${code} ${
                    SalaryPay ? ` / ${SalaryPay}` : ""
                  }`;
                else {
                  return "-";
                }
              })(),
              gridSize: "small",
            },

            {
              label: "Currency",
              value: (() => {
                try {
                  const currency = jobdata?.JD_Currency;
                  return `${currency?.Code || ""} - ${currency?.Name || ""}`;
                } catch {
                  return "";
                }
              })(),
              gridSize: "small",
            },
            {
              label: "Number of Openings",
              value: jobdata?.JD_NoofOpening || "-",
              gridSize: "small",
            },
            {
              label: "Open Date",
              value: jobdata?.JD_OpenDate?.split("T")[0] || "-",
              gridSize: "small",
            },
            {
              label: "Close Date",
              value: jobdata?.JD_CloseDate?.split("T")[0] || "-",
              gridSize: "small",
            },
            {
              label: "Qualification",
              value: (() => {
                try {
                  const data = jobdata?.JD_Qualification;
                  const parsed =
                    typeof data === "string" ? JSON.parse(data) : data;
                  return parsed?.Name || "-";
                } catch {
                  return "-";
                }
              })(),
              gridSize: "small",
            },
            {
              label: "Age Limit",
              value:
                jobdata?.JD_MinAge && jobdata?.JD_MaxAge
                  ? `${jobdata.JD_MinAge} - ${jobdata.JD_MaxAge}`
                  : jobdata?.JD_MinAge
                  ? `${jobdata.JD_MinAge}`
                  : jobdata?.JD_MaxAge
                  ? `${jobdata.JD_MaxAge}`
                  : "-",
              gridSize: "small",
            },
            {
              label: "Gender",
              value: (() => {
                try {
                  const parsed = JSON.parse(jobdata?.JD_Gender);
                  return Array.isArray(parsed)
                    ? parsed
                        .filter((g) => g?.Name) // Filter out entries with null/undefined Name
                        .map((g) => g.Name)
                        .join(", ") || "-" // Fallback if array becomes empty after filtering
                    : "-";
                } catch {
                  return "-";
                }
              })(),
              gridSize: "small",
            },

            {
              label: "Additional Benefits",
              key: "additionalBenefits",
              value: jobdata?.JD_AdditionalBenefits || "-",
              gridSize: "large",
            },
            {
              label: "Additional Requirements",
              key: "additionalRequirements",
              value: (() => {
                try {
                  const parsed = JSON.parse(jobdata?.JD_AdditionalRequirements);
                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              })(),
              gridSize: "large",
            },
          ].map((item, index) => (
            <Grid
              item
              {...(item.gridSize === "large"
                ? { xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }
                : { xs: 12, sm: 6, md: 4, lg: 4, xl: 3 })}
              key={index}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontFamily: "Geist, sans-serif",
                    fontSize: "18px",
                    mb: 0.5,
                  }}
                >
                  {item.label}
                </Typography>

                {item.key === "additionalBenefits" ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {item.value !== "-" ? (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontFamily: "Geist, sans-serif",
                          fontSize: "16px",
                        }}
                      >
                        {item?.value}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontFamily: "Geist, sans-serif",
                          fontSize: "16px",
                        }}
                      >
                        -
                      </Typography>
                    )}
                  </Stack>
                ) : item.key === "additionalRequirements" ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {item.value.filter((req) => req?.Name).length > 0 ? (
                      item.value
                        .filter((req) => req?.Name)
                        .map((req, idx) => (
                          <Chip
                            key={idx}
                            label={req.Name}
                            style={{ margin: "5px 5px 5px 0px" }}
                          />
                        ))
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontFamily: "Geist, sans-serif",
                          fontSize: "16px",
                        }}
                      >
                        -
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontFamily: "Geist, sans-serif",
                      fontSize: "16px",
                    }}
                  >
                    {item?.value}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Additional Details Section */}
      <Box
        sx={{
          mb: 3,
          border: "1px solid lightgrey",
          p: 3,
          borderRadius: "20px",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            px: 2,
            py: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          >
            Client Details
          </Typography>
        </Box>

        <Grid container spacing={2}>

          {[
            {
              label: "Name",
              value: (() => {
                try {
                  return jobdata?.JD_Client?.Name || "-";
                } catch {
                  return "-";
                }
              })(),
            },
            {
              label: "Email ID",
              value: (() => {
                try {
                  return jobdata?.JD_Client?.Email || "-";
                } catch {
                  return "-";
                }
              })(),
            },
            {
              label: "Phone Number",
              value: (() => {
                try {
                  return jobdata?.JD_Client?.Phone || "-";
                } catch {
                  return "-";
                }
              })(),
            },
            // {
            //   label: "Hospital / Company",
            //   value: jobdata?.JD_Company ? jobdata?.JD_Company : "-",
            // },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={index}>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontFamily: "Geist, sans-serif",
                    fontSize: "18px",
                    mb: 0.5,
                  }}
                >
                  {item?.label}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Geist, sans-serif",
                    fontSize: "16px",
                  }}
                >
                  {item?.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* <CandidateList jobId={id} /> */}
    </Paper>
  );
};

export default ClientJobDetails;
